<template>
  <div class="content" v-if="loading">
    <div class="section mt-0">
      <h1 class="title title--h1 title__separate">Blog</h1>
    </div>
    <div class="text-center">
      <div class="loading-spinner"></div>
      <p class="loading-text">Loading post...</p>
    </div>
  </div>

  <div class="content" v-else-if="post">
    <div class="mb-3">
      <router-link to="/blog" class="link-btn">
        <i class="icon-arrow-left"></i> Back to Blog
      </router-link>
    </div>
    <div class="section mt-0">
      <h1 class="title title--h1 title__separate">{{ post.title }}</h1>
    </div>
    <div class="pb-3">
      <div class="blog-post__header">
        <span class="blog-post__date">
          <i class="icon-calendar"></i> {{ post.day }} {{ post.month }}
        </span>
        <span v-if="post.tags && post.tags.length" class="blog-post__tags">
          <span class="blog-post__tag" v-for="tag in post.tags" :key="tag">{{ tag }}</span>
        </span>
      </div>
    </div>
    <div class="blog-post__image-wrap" v-if="post.image">
      <img :src="post.image" :alt="post.alt" class="blog-post__image">
    </div>
    <div v-if="post.content" class="blog-post__content mt-4" v-html="post.content"></div>
    <div v-else class="blog-post__content mt-4">
      <p>{{ post.excerpt }}</p>
    </div>
  </div>

  <div class="content" v-else>
    <div class="mb-3">
      <router-link to="/blog" class="link-btn">
        <i class="icon-arrow-left"></i> Back to Blog
      </router-link>
    </div>
    <div class="section mt-0">
      <h1 class="title title--h1 title__separate">Blog Post Not Found</h1>
    </div>
    <p>The blog post you're looking for doesn't exist.</p>
  </div>
</template>

<script>
import { fetchBlogPost } from '@/services/hashnode'
import fallbackPosts from '@/data/blogPosts'

export default {
  name: 'BlogPostView',
  data() {
    return {
      post: null,
      loading: true
    }
  },
  created() {
    this.loadPost()
  },
  watch: {
    '$route.params.slug'() {
      this.loadPost()
    }
  },
  methods: {
    async loadPost() {
      this.loading = true
      const slug = this.$route.params.slug
      try {
        this.post = await fetchBlogPost(slug)
      } catch {
        this.post = null
      }
      if (!this.post) {
        const fallback = fallbackPosts.find(p => p.slug === slug)
        if (fallback) this.post = fallback
      }
      this.loading = false
    }
  }
}
</script>

<style scoped>
.blog-post__header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  color: #8697a8;
  font-size: 14px;
}
.blog-post__date i {
  margin-right: 4px;
}
.blog-post__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.blog-post__tag {
  background: rgba(13, 134, 255, 0.12);
  color: #0d86ff;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}
.blog-post__image-wrap {
  border-radius: 12px;
  overflow: hidden;
}
.blog-post__image {
  width: 100%;
  display: block;
  border-radius: 12px;
}
.blog-post__content {
  font-size: 16px;
  line-height: 1.8;
  color: #d4d4e0;
}
.loading-spinner {
  width: 40px;
  height: 40px;
  margin: 20px auto;
  border: 3px solid rgba(134, 151, 168, 0.3);
  border-top-color: #0d86ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
.loading-text {
  color: #8697a8;
  font-size: 14px;
  margin-top: 8px;
}
</style>

<style>
/* Unscoped styles for Hashnode HTML content rendered via v-html */
.blog-post__content h1,
.blog-post__content h2,
.blog-post__content h3,
.blog-post__content h4 {
  color: #f0f0f6;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-weight: 600;
}
.blog-post__content h2 {
  font-size: 1.5em;
  border-bottom: 1px solid rgba(134, 151, 168, 0.2);
  padding-bottom: 0.3em;
}
.blog-post__content h3 {
  font-size: 1.25em;
}
.blog-post__content p {
  margin-bottom: 1em;
}
.blog-post__content a {
  color: #0d86ff;
  text-decoration: none;
}
.blog-post__content a:hover {
  text-decoration: underline;
}
.blog-post__content img {
  max-width: 100%;
  border-radius: 8px;
  margin: 1em 0;
}
.blog-post__content pre {
  background: #1a1a2e;
  border: 1px solid rgba(134, 151, 168, 0.2);
  border-radius: 8px;
  padding: 16px;
  overflow-x: auto;
  margin: 1em 0;
  font-size: 14px;
  line-height: 1.6;
}
.blog-post__content code {
  font-family: 'Fira Code', 'Consolas', monospace;
  font-size: 0.9em;
}
.blog-post__content :not(pre) > code {
  background: rgba(13, 134, 255, 0.1);
  color: #7ec8f2;
  padding: 2px 6px;
  border-radius: 4px;
}
.blog-post__content blockquote {
  border-left: 3px solid #0d86ff;
  margin: 1em 0;
  padding: 0.5em 1em;
  color: #8697a8;
  background: rgba(13, 134, 255, 0.05);
  border-radius: 0 8px 8px 0;
}
.blog-post__content ul,
.blog-post__content ol {
  padding-left: 1.5em;
  margin-bottom: 1em;
}
.blog-post__content li {
  margin-bottom: 0.4em;
}
.blog-post__content table {
  width: 100%;
  border-collapse: collapse;
  margin: 1em 0;
}
.blog-post__content th,
.blog-post__content td {
  border: 1px solid rgba(134, 151, 168, 0.2);
  padding: 8px 12px;
  text-align: left;
}
.blog-post__content th {
  background: rgba(13, 134, 255, 0.08);
  color: #f0f0f6;
  font-weight: 600;
}
.blog-post__content hr {
  border: none;
  border-top: 1px solid rgba(134, 151, 168, 0.2);
  margin: 2em 0;
}
</style>
